import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Button, Chip, Dialog, Portal, TextInput,
} from 'react-native-paper';
import { View } from 'react-native';
import { HStack, Text, VStack } from 'native-base';
import { useToast } from 'react-native-toast-notifications';
import { format } from 'date-fns';
import { START_TRIP_PAUSE, END_TRIP_PAUSE } from '../../gql/trip/mutation';
import { FindManyTrips } from '../../gql/trip/query';
import { getActivePause, pauseEndOf } from '../../utils/tripPauses';

const DEFAULT_PAUSE_MINUTES = 15;
const QUICK_DURATIONS = [15, 30, 45, 60];

/** Driver-only: declare a pause (rest, fuel…) or resume early. */
function TripPauseControls({ trip }) {
  const toast = useToast();
  const [dialogVisible, setDialogVisible] = useState(false);
  const [minutes, setMinutes] = useState(String(DEFAULT_PAUSE_MINUTES));

  // Re-evaluate "is the pause still running" when it expires.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(timer);
  }, []);
  const activePause = getActivePause(trip.pauses, now);

  const mutationOptions = {
    refetchQueries: [FindManyTrips],
    onError: (error) => toast.show(error.message, { type: 'danger' }),
  };
  const [startPause, { loading: starting }] = useMutation(START_TRIP_PAUSE, {
    ...mutationOptions,
    onCompleted: () => {
      setDialogVisible(false);
      setMinutes(String(DEFAULT_PAUSE_MINUTES));
      toast.show('Pause enregistrée', { type: 'success' });
    },
  });
  const [endPause, { loading: ending }] = useMutation(END_TRIP_PAUSE, {
    ...mutationOptions,
    onCompleted: () => toast.show('Reprise enregistrée', { type: 'success' }),
  });

  const parsedMinutes = Number(minutes);
  const isValid = Number.isInteger(parsedMinutes) && parsedMinutes >= 1 && parsedMinutes <= 240;

  return (
    <VStack space={2}>
      {activePause ? (
        <HStack
          alignItems="center"
          justifyContent="space-between"
          bg="orange.100"
          p={3}
          borderRadius="md"
        >
          <Text fontSize="sm" fontWeight="bold" color="orange.800">
            {`En pause jusqu'à ${format(new Date(pauseEndOf(activePause)), 'HH:mm')}`}
          </Text>
          <Button
            mode="contained"
            compact
            loading={ending}
            disabled={ending}
            onPress={() => endPause({ variables: { tripId: Number(trip.id) } })}
          >
            Reprendre
          </Button>
        </HStack>
      ) : (
        <Button mode="outlined" icon="coffee-outline" onPress={() => setDialogVisible(true)}>
          Signaler une pause
        </Button>
      )}

      <Portal>
        <Dialog visible={dialogVisible} onDismiss={() => setDialogVisible(false)}>
          <Dialog.Title>Signaler une pause</Dialog.Title>
          <Dialog.Content>
            <Text mb={3}>
              Les estimations d&apos;arrivée en tiendront compte. Durée de la pause :
            </Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {QUICK_DURATIONS.map((d) => (
                <Chip
                  key={d}
                  selected={parsedMinutes === d}
                  onPress={() => setMinutes(String(d))}
                >
                  {`${d} min`}
                </Chip>
              ))}
            </View>
            <TextInput
              label="Autre durée (minutes)"
              value={minutes}
              onChangeText={(value) => setMinutes(value.replace(/[^0-9]/g, ''))}
              keyboardType="number-pad"
              error={!isValid}
            />
            {!isValid && (
              <Text fontSize="xs" color="red.500" mt={1}>Entre 1 et 240 minutes.</Text>
            )}
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>Annuler</Button>
            <Button
              loading={starting}
              disabled={!isValid || starting}
              onPress={() => startPause({
                variables: { tripId: Number(trip.id), durationMinutes: parsedMinutes },
              })}
            >
              Démarrer la pause
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </VStack>
  );
}

export default TripPauseControls;
